import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import firebaseService from '../services/firebaseService';
import { formatCurrency, calculateITC } from '../services/gstCalculations';
import { 
  PlusIcon, 
  DocumentArrowUpIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  FunnelIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [filters, setFilters] = useState({
    quarter: '',
    year: '',
    supplierId: '',
    paymentMode: ''
  });

  const [formData, setFormData] = useState({
    purchaseDate: new Date(),
    supplierId: '',
    supplierName: '',
    invoiceNumber: '',
    purchaseAmount: '',
    gstAmount: '',
    paymentMode: 'Bank',
    description: '',
    productName: '',
    imeiNumber: '',
    quantity: 1
  });

  const [invoiceFile, setInvoiceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const paymentModes = ['Bank', 'Credit Card', 'Cash', 'UPI', 'Cheque'];

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [purchasesData, suppliersData] = await Promise.all([
        firebaseService.getPurchases(filters),
        firebaseService.getSuppliers()
      ]);
      
      setPurchases(purchasesData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error loading purchases:', error);
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      purchaseDate: new Date(),
      supplierId: '',
      supplierName: '',
      invoiceNumber: '',
      purchaseAmount: '',
      gstAmount: '',
      paymentMode: 'Bank',
      description: '',
      productName: '',
      imeiNumber: '',
      quantity: 1
    });
    setInvoiceFile(null);
    setEditingPurchase(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.purchaseAmount || !formData.gstAmount) {
      return toast.error('Please fill in all required fields');
    }

    if (parseFloat(formData.gstAmount) >= parseFloat(formData.purchaseAmount)) {
      return toast.error('GST amount cannot be greater than or equal to purchase amount');
    }

    setSubmitting(true);

    try {
      const purchaseData = {
        ...formData,
        purchaseAmount: parseFloat(formData.purchaseAmount),
        gstAmount: parseFloat(formData.gstAmount)
      };

      if (editingPurchase) {
        await firebaseService.updatePurchase(editingPurchase.id, purchaseData, invoiceFile);
        toast.success('Purchase updated successfully');
      } else {
        await firebaseService.addPurchase(purchaseData, invoiceFile);
        toast.success('Purchase added successfully');
      }

      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving purchase:', error);
      toast.error('Failed to save purchase');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      purchaseDate: purchase.purchaseDate || new Date(),
      supplierId: purchase.supplierId || '',
      supplierName: purchase.supplierName || '',
      invoiceNumber: purchase.invoiceNumber || '',
      purchaseAmount: purchase.purchaseAmount?.toString() || '',
      gstAmount: purchase.gstAmount?.toString() || '',
      paymentMode: purchase.paymentMode || 'Bank',
      description: purchase.description || '',
      productName: purchase.productName || '',
      imeiNumber: purchase.imeiNumber || '',
      quantity: purchase.quantity || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to delete this purchase?')) {
      return;
    }

    try {
      await firebaseService.deletePurchase(purchaseId);
      toast.success('Purchase deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting purchase:', error);
      toast.error('Failed to delete purchase');
    }
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setInvoiceFile(file);
    } else {
      toast.error('Please upload a PDF file only');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const calculatePreview = () => {
    if (formData.purchaseAmount && formData.gstAmount) {
      return calculateITC(formData.purchaseAmount, formData.gstAmount);
    }
    return null;
  };

  const previewData = calculatePreview();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Purchases
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your purchase invoices and track ITC
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Purchase
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={filters.quarter}
              onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
              className="input"
            >
              <option value="">All Quarters</option>
              <option value="Q1">Q1 (Apr-Jun)</option>
              <option value="Q2">Q2 (Jul-Sep)</option>
              <option value="Q3">Q3 (Oct-Dec)</option>
              <option value="Q4">Q4 (Jan-Mar)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="input"
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>{year}-{(year + 1).toString().substr(-2)}</option>
                );
              })}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
            <select
              value={filters.supplierId}
              onChange={(e) => setFilters({ ...filters, supplierId: e.target.value })}
              className="input"
            >
              <option value="">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.supplierName}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
            <select
              value={filters.paymentMode}
              onChange={(e) => setFilters({ ...filters, paymentMode: e.target.value })}
              className="input"
            >
              <option value="">All Payment Modes</option>
              {paymentModes.map(mode => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Purchase Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPurchase ? 'Edit Purchase' : 'Add New Purchase'}
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Date *
                    </label>
                    <DatePicker
                      selected={formData.purchaseDate}
                      onChange={(date) => setFormData({ ...formData, purchaseDate: date })}
                      className="input w-full"
                      dateFormat="dd/MM/yyyy"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier *
                    </label>
                    <select
                      value={formData.supplierId}
                      onChange={(e) => {
                        const supplier = suppliers.find(s => s.id === e.target.value);
                        setFormData({
                          ...formData,
                          supplierId: e.target.value,
                          supplierName: supplier?.supplierName || ''
                        });
                      }}
                      className="input"
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.supplierName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Number *
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Mode
                    </label>
                    <select
                      value={formData.paymentMode}
                      onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                      className="input"
                    >
                      {paymentModes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Purchase Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.purchaseAmount}
                      onChange={(e) => setFormData({ ...formData, purchaseAmount: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.gstAmount}
                      onChange={(e) => setFormData({ ...formData, gstAmount: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={formData.productName}
                      onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                      className="input"
                      placeholder="e.g., Samsung Galaxy S23"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IMEI Number *
                    </label>
                    <input
                      type="text"
                      value={formData.imeiNumber}
                      onChange={(e) => setFormData({ ...formData, imeiNumber: e.target.value })}
                      className="input"
                      placeholder="15-digit IMEI number"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                      className="input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    rows="2"
                    placeholder="Additional product details, warranty info, etc."
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice PDF
                  </label>
                  <div
                    {...getRootProps()}
                    className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-gray-400 ${
                      isDragActive ? 'border-primary-400 bg-primary-50' : ''
                    }`}
                  >
                    <div className="space-y-1 text-center">
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <input {...getInputProps()} />
                        <p>
                          {invoiceFile
                            ? `Selected: ${invoiceFile.name}`
                            : isDragActive
                            ? 'Drop the PDF file here...'
                            : 'Drag and drop a PDF file here, or click to select'
                          }
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 10MB</p>
                    </div>
                  </div>
                </div>

                {/* Calculation Preview */}
                {previewData && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Calculation Preview</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Base Amount:</span>
                        <span className="ml-2 font-medium">{formatCurrency(previewData.baseAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">ITC Available:</span>
                        <span className="ml-2 font-medium text-green-600">{formatCurrency(previewData.itcAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting ? (
                      <div className="spinner mr-2"></div>
                    ) : null}
                    {editingPurchase ? 'Update' : 'Add'} Purchase
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Purchases Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Purchase Records</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {purchases.length} purchase{purchases.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : purchases.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Invoice No.</th>
                  <th>Amount</th>
                  <th>GST</th>
                  <th>ITC</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      {purchase.purchaseDate
                        ? new Date(purchase.purchaseDate).toLocaleDateString('en-IN')
                        : 'N/A'
                      }
                    </td>
                    <td className="font-medium">{purchase.supplierName}</td>
                    <td>{purchase.invoiceNumber}</td>
                    <td>{formatCurrency(purchase.totalAmount || 0)}</td>
                    <td>{formatCurrency(purchase.gstAmount || 0)}</td>
                    <td className="text-green-600 font-medium">
                      {formatCurrency(purchase.itcAmount || 0)}
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {purchase.paymentMode}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        {purchase.invoiceUrl && (
                          <a
                            href={purchase.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900"
                            title="View Invoice"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleEdit(purchase)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(purchase.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No purchases</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first purchase.</p>
            <div className="mt-6">
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Purchase
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Purchases;
